import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { Check, Loader2, RotateCcw, Sparkles, Trash2, Wand2, Workflow } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import {
  createAction,
  createRule,
  deleteAction,
  deleteRule,
  getAction,
  getRule,
  normalizeAction,
  normalizeRule,
  readAuthorResult,
  startAuthor,
  stripServerFields,
  validationMessages,
  type ActionDef,
  type AuthoredRef,
  type InteractionRule,
  type RegistryKind,
} from '@/api/actions'
import { cancelJob } from '@/api/jobs'
import { waitForJob, type JobPhaseUpdate } from '@/api/skel'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Field, Input, Textarea } from '@/components/ui/input'
import { toast } from '@/components/ui/toast'
import { cn } from '@/components/ui/utils'

import { describeAction, describeRule } from './describe'
import { invalidateLists, storeDoc } from './hooks'
import { RoleChips } from './parts'
import { PreviewPanel } from './PreviewPanel'

/**
 * "Create with AI" (R4/R7, §11.7): say what it should be in words → the author job writes it
 * (live phases) → it plays in the preview player (a rule: a little scene where its event happens)
 * and reads back in plain words → **Keep** (open it), **Try again** (the same words, a fresh
 * attempt; this one is removed) or **Discard** (removed). The job saves what it made (`origin: 'ai'`), so Discard deletes it; if a
 * job ever answers with an unsaved document instead, Keep is what creates it.
 */

type Doc = ActionDef | InteractionRule

type Stage =
  | { s: 'form' }
  | { s: 'working'; jobId: number | null; phase: string | null; msg: string | null; pct: number | null }
  | { s: 'review'; ref: AuthoredRef; doc: Doc }
  | { s: 'failed'; messages: string[] }

export interface CreateWithAIDialogProps {
  open: boolean
  onOpenChange(open: boolean): void
  initialKind: RegistryKind
  initialDescription?: string
  /** After Keep. Default: open the new action/rule. */
  onKept?(ref: { kind: RegistryKind; id: string }): void
  /** Job poll period (ms); tests make it short. */
  pollMs?: number
  /** Also follow the job's SSE stream (default true). */
  stream?: boolean
}

const PHASES = ['write', 'moves', 'check', 'save'] as const
type PhaseKey = (typeof PHASES)[number]

/** A job's technical phase → which of the four steps the parent sees. */
export function phaseStep(phase: string | null | undefined): PhaseKey {
  const p = (phase ?? '').toLowerCase()
  if (/save|done|finish|store/.test(p)) return 'save'
  if (/valid|dry|check|repair|test|try|render/.test(p)) return 'check'
  if (/clip|anim|move|template|pose/.test(p)) return 'moves'
  return 'write'
}

function phaseLabel(key: PhaseKey, kind: RegistryKind): string {
  switch (key) {
    case 'write':
      return kind === 'rule' ? 'Writing the rule' : 'Writing the action'
    case 'moves':
      return 'Teaching the moves'
    case 'check':
      return 'Trying it out'
    case 'save':
      return 'Saving'
  }
}

export function CreateWithAIDialog({ open, onOpenChange, initialKind, initialDescription = '', onKept, pollMs, stream = true }: CreateWithAIDialogProps) {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const [kind, setKind] = useState<RegistryKind>(initialKind)
  const [description, setDescription] = useState(initialDescription)
  const [name, setName] = useState('')
  const [stage, setStage] = useState<Stage>({ s: 'form' })
  const [busy, setBusy] = useState<'keep' | 'discard' | 'retry' | null>(null)
  const abort = useRef<AbortController | null>(null)

  // Every opening starts fresh from what the page asked for.
  useEffect(() => {
    if (!open) return
    setKind(initialKind)
    setDescription(initialDescription)
    setName('')
    setStage({ s: 'form' })
    setBusy(null)
  }, [open, initialKind, initialDescription])

  useEffect(() => () => abort.current?.abort(), [])

  const canStart = description.trim().length >= 3

  const run = async (): Promise<void> => {
    abort.current?.abort()
    const ctrl = new AbortController()
    abort.current = ctrl
    setStage({ s: 'working', jobId: null, phase: null, msg: 'Starting…', pct: null })
    try {
      const { job_id } = await startAuthor({ kind, description, name })
      if (ctrl.signal.aborted) return
      setStage({ s: 'working', jobId: job_id, phase: null, msg: 'Waiting for its turn…', pct: null })
      const onProgress = (u: JobPhaseUpdate): void => {
        if (ctrl.signal.aborted) return
        setStage({ s: 'working', jobId: job_id, phase: u.phase, msg: u.status === 'queued' ? 'Waiting for its turn…' : u.msg, pct: u.pct })
      }
      const result = await waitForJob<Record<string, unknown>>(job_id, onProgress, ctrl.signal, { intervalMs: pollMs ?? 1_000, stream })
      if (ctrl.signal.aborted) return
      const ref = readAuthorResult(kind, result)
      const doc = await loadDoc(ref)
      if (ctrl.signal.aborted) return
      if (ref.saved && ref.id) {
        storeDoc(qc, ref.kind, doc)
        void invalidateLists(qc, ref.kind)
      }
      setStage({ s: 'review', ref, doc })
    } catch (err) {
      if (ctrl.signal.aborted) return
      setStage({ s: 'failed', messages: validationMessages(err) })
    }
  }

  const loadDoc = async (ref: AuthoredRef): Promise<Doc> => {
    if (ref.saved && ref.id) {
      try {
        return ref.kind === 'rule' ? await getRule(ref.id) : await getAction(ref.id)
      } catch (err) {
        if (!ref.doc) throw err
      }
    }
    if (ref.doc && typeof ref.doc.id === 'string') {
      const raw = { origin: 'ai' as const, ...ref.doc, id: ref.doc.id }
      return ref.kind === 'rule' ? normalizeRule(raw) : normalizeAction(raw)
    }
    throw new Error('It finished, but did not say what it made. Please try again.')
  }

  /** Remove what the job saved (Discard, Try again). */
  const removeMade = async (): Promise<void> => {
    if (stage.s !== 'review' || !stage.ref.saved) return
    const id = stage.doc.id
    if (stage.ref.kind === 'rule') await deleteRule(id)
    else await deleteAction(id)
    void invalidateLists(qc, stage.ref.kind)
  }

  const keep = async (): Promise<void> => {
    if (stage.s !== 'review') return
    setBusy('keep')
    try {
      let id = stage.doc.id
      if (!stage.ref.saved) {
        const body = stripServerFields(stage.doc as unknown as Record<string, unknown>)
        const made = stage.ref.kind === 'rule' ? await createRule(body) : await createAction(body)
        storeDoc(qc, stage.ref.kind, made)
        id = made.id
      }
      void invalidateLists(qc, stage.ref.kind)
      const label = ('label' in stage.doc && typeof stage.doc.label === 'string' && stage.doc.label) || id
      toast.success(`Kept “${label}”`, { description: stage.ref.kind === 'rule' ? 'Stories follow this rule from now on.' : 'Stories can use it from now on.' })
      onOpenChange(false)
      if (onKept) onKept({ kind: stage.ref.kind, id })
      else void navigate({ to: '/actions/$id', params: { id }, search: { kind: stage.ref.kind } })
    } catch (err) {
      toast.error('Could not keep it', { description: validationMessages(err).join(' ') })
    } finally {
      setBusy(null)
    }
  }

  const discard = async (): Promise<void> => {
    setBusy('discard')
    try {
      await removeMade()
      onOpenChange(false)
    } catch (err) {
      toast.error('Could not remove it', { description: validationMessages(err).join(' ') })
    } finally {
      setBusy(null)
    }
  }

  const tryAgain = async (): Promise<void> => {
    setBusy('retry')
    try {
      await removeMade()
    } catch {
      // A leftover is harmless (it can be deleted from its page); the new attempt matters more.
    } finally {
      setBusy(null)
    }
    await run()
  }

  const cancelWork = (): void => {
    const jobId = stage.s === 'working' ? stage.jobId : null
    abort.current?.abort()
    if (jobId != null) void cancelJob(jobId).catch(() => undefined)
    setStage({ s: 'form' })
  }

  const noun = kind === 'rule' ? 'world rule' : 'action'

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o && stage.s === 'working') cancelWork()
        onOpenChange(o)
      }}
    >
      <DialogContent size="lg" data-testid="create-with-ai-dialog">
        <DialogHeader>
          <DialogTitle>{stage.s === 'review' ? `Here is your new ${noun}` : `Create ${kind === 'rule' ? 'a world rule' : 'an action'} with AI`}</DialogTitle>
          <DialogDescription>
            {stage.s === 'review'
              ? kind === 'rule'
                ? 'Read it back. Keep it and every new story follows it.'
                : 'Watch it played by puppets. Keep it and stories can use it.'
              : 'Say it in your own words. The studio writes it, teaches the puppets any new moves, and tries it out before you decide.'}
          </DialogDescription>
        </DialogHeader>

        {stage.s === 'form' ? (
          <form
            className="flex flex-col gap-4"
            onSubmit={(e) => {
              e.preventDefault()
              if (canStart) void run()
            }}
          >
            <div role="radiogroup" aria-label="What to make" className="grid gap-2 sm:grid-cols-2">
              <KindOption kind="action" current={kind} onPick={setKind} icon={<Wand2 aria-hidden />} title="An action" hint="Something puppets do — alone, together, or with things." />
              <KindOption kind="rule" current={kind} onPick={setKind} icon={<Workflow aria-hidden />} title="A world rule" hint="How the world answers — splashes, falls, doors that open." />
            </div>
            <Field label="Describe it" htmlFor="author-description" hint={kind === 'rule' ? 'Say what happens, and when.' : 'Who does what, with what or whom.'}>
              <Textarea
                id="author-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                placeholder={kind === 'rule' ? 'e.g. when something heavy lands on the ground, a puff of dust goes up' : 'e.g. two friends juggle a ball between them, then take a bow'}
                autoFocus
              />
            </Field>
            <Field label="Name (optional)" htmlFor="author-name" hint="Leave it empty and the studio picks one.">
              <Input id="author-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={60} placeholder={kind === 'rule' ? 'Dust when it lands' : 'Juggle'} />
            </Field>
            <DialogFooter>
              <Button variant="secondary" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={!canStart}>
                <Sparkles aria-hidden />
                Create
              </Button>
            </DialogFooter>
          </form>
        ) : null}

        {stage.s === 'working' ? (
          <div className="flex flex-col gap-4" data-testid="author-progress">
            <Phases kind={kind} current={phaseStep(stage.phase)} />
            <div className="h-2 overflow-hidden rounded-full bg-paper-2" aria-hidden>
              <div
                className={cn('h-full rounded-full bg-info transition-[width]', stage.pct == null && 'w-1/4 animate-pulse-soft')}
                style={stage.pct != null ? { width: `${Math.max(4, Math.round(Math.min(1, Math.max(0, stage.pct)) * 100))}%` } : undefined}
              />
            </div>
            <p className="text-sm text-ink-2" role="status">
              {stage.msg ?? 'Working…'}
            </p>
            <DialogFooter>
              <Button variant="secondary" onClick={cancelWork}>
                Stop
              </Button>
            </DialogFooter>
          </div>
        ) : null}

        {stage.s === 'failed' ? (
          <div className="flex flex-col gap-4" role="alert" data-testid="author-failed">
            <p className="text-ink">That didn’t work.</p>
            <ul className="list-disc pl-5 text-sm text-danger">
              {stage.messages.map((m, i) => (
                <li key={`${i}:${m}`}>{m}</li>
              ))}
            </ul>
            <DialogFooter>
              <Button variant="secondary" onClick={() => setStage({ s: 'form' })}>
                Change the words
              </Button>
              <Button variant="primary" onClick={() => void run()}>
                <RotateCcw aria-hidden />
                Try again
              </Button>
            </DialogFooter>
          </div>
        ) : null}

        {stage.s === 'review' ? (
          <Review stage={stage} busy={busy} onKeep={() => void keep()} onTryAgain={() => void tryAgain()} onDiscard={() => void discard()} />
        ) : null}
      </DialogContent>
    </Dialog>
  )
}

function KindOption({ kind, current, onPick, icon, title, hint }: { kind: RegistryKind; current: RegistryKind; onPick(k: RegistryKind): void; icon: React.ReactNode; title: string; hint: string }) {
  const on = kind === current
  return (
    <button
      type="button"
      role="radio"
      aria-checked={on}
      onClick={() => onPick(kind)}
      className={cn(
        'flex items-start gap-3 rounded-xl border p-3 text-left transition-colors focus-visible:focus-ring [&_svg]:mt-0.5 [&_svg]:size-5 [&_svg]:shrink-0',
        on ? 'border-accent bg-accent-soft text-ink' : 'border-line bg-paper text-ink-2 hover:bg-paper-2',
      )}
    >
      {icon}
      <span className="flex flex-col">
        <span className="text-sm font-semibold text-ink">{title}</span>
        <span className="text-xs">{hint}</span>
      </span>
    </button>
  )
}

function Phases({ kind, current }: { kind: RegistryKind; current: PhaseKey }) {
  const at = PHASES.indexOf(current)
  return (
    <ol className="flex flex-col gap-2" aria-label="Progress">
      {PHASES.map((p, i) => {
        const done = i < at
        const now = i === at
        return (
          <li key={p} className={cn('flex items-center gap-2 text-sm', done ? 'text-ink-2' : now ? 'font-medium text-ink' : 'text-ink-3')} aria-current={now ? 'step' : undefined} data-phase={p} data-state={done ? 'done' : now ? 'now' : 'next'}>
            <span className={cn('inline-flex size-5 items-center justify-center rounded-full', done ? 'bg-ok-soft text-ok' : now ? 'bg-info-soft text-info' : 'bg-paper-2')} aria-hidden>
              {done ? <Check className="size-3.5" /> : now ? <Loader2 className="size-3.5 animate-spin" /> : null}
            </span>
            {phaseLabel(p, kind)}
          </li>
        )
      })}
    </ol>
  )
}

function Review({
  stage,
  busy,
  onKeep,
  onTryAgain,
  onDiscard,
}: {
  stage: Extract<Stage, { s: 'review' }>
  busy: 'keep' | 'discard' | 'retry' | null
  onKeep(): void
  onTryAgain(): void
  onDiscard(): void
}) {
  const { ref, doc } = stage
  const isRule = ref.kind === 'rule'
  const rule = isRule ? (doc as InteractionRule) : null
  const action = isRule ? null : (doc as ActionDef)
  const title = action ? action.label : rule?.label || rule?.id || ''
  return (
    <div className="flex flex-col gap-4" data-testid="author-review">
      <div className="flex flex-col gap-1">
        <h3 className="text-xl font-semibold text-ink">{title}</h3>
        {doc.description ? <p className="text-sm text-ink-2">{doc.description}</p> : null}
      </div>
      {action ? (
        <>
          <RoleChips roles={action.roles} />
          <PreviewPanel action={action} autoPlay compact />
          <ActionGist action={action} />
        </>
      ) : rule ? (
        <>
          <PreviewPanel kind="rule" action={{ id: rule.id, label: rule.label || rule.id, roles: {} }} autoPlay compact />
          <RuleGist rule={rule} />
        </>
      ) : null}
      <DialogFooter className="sm:justify-between">
        <Button variant="ghost" className="text-danger" onClick={onDiscard} loading={busy === 'discard'} disabled={busy !== null && busy !== 'discard'}>
          {busy === 'discard' ? null : <Trash2 aria-hidden />}
          Discard
        </Button>
        <div className="flex flex-col-reverse gap-2 sm:flex-row">
          <Button variant="secondary" onClick={onTryAgain} loading={busy === 'retry'} disabled={busy !== null && busy !== 'retry'}>
            {busy === 'retry' ? null : <RotateCcw aria-hidden />}
            Try again
          </Button>
          <Button variant="primary" onClick={onKeep} loading={busy === 'keep'} disabled={busy !== null && busy !== 'keep'}>
            {busy === 'keep' ? null : <Check aria-hidden />}
            Keep
          </Button>
        </div>
      </DialogFooter>
    </div>
  )
}

function ActionGist({ action }: { action: ActionDef }) {
  const sum = describeAction(action)
  const lines = sum.steps.flatMap((s) => s.lines).slice(0, 6)
  if (lines.length === 0) return null
  return (
    <ol className="list-decimal space-y-0.5 pl-5 text-sm text-ink-2" aria-label="What happens">
      {lines.map((l, i) => (
        <li key={`${i}:${l}`}>{l}</li>
      ))}
    </ol>
  )
}

function RuleGist({ rule }: { rule: InteractionRule }) {
  const sum = describeRule(rule)
  return (
    <div className="flex flex-col gap-2 text-sm" data-testid="rule-gist">
      <p className="font-medium text-ink">{sum.when}:</p>
      <ul className="list-disc space-y-0.5 pl-5 text-ink-2">
        {sum.then.flatMap((s) => s.lines).map((l, i) => (
          <li key={`${i}:${l}`}>{l}</li>
        ))}
        {sum.after.map((l, i) => (
          <li key={`a${i}:${l}`}>{l}</li>
        ))}
      </ul>
    </div>
  )
}
